import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = [
    "input",
    "image",
    "zoomSlider",
    "rotationSlider",
    "zoomValue",
    "rotationValue",
    "cropButton",
    "cancelButton",
    "avatarDisplay",
  ];

  static values = {
    aspectRatio: { type: Number, default: 1 },
    cropShape: { type: String, default: "round" },
    uploadUrl: String,
    updateUrl: String,
    modalId: { type: String, default: "image-cropper-modal" },
    validTypes: {
      type: Array,
      default: ["image/png", "image/jpeg", "image/webp"],
    },
    maxSize: { type: Number, default: 2097152 },
    // Textes UI i18n (audit 2026-08-04, A6-F6) : defaults FR, alimentés par
    // data-image-cropper-*-value avec t() — clés stimulus.image_cropper.*
    invalidTypeMessage: { type: String, default: "Veuillez sélectionner une image PNG, JPEG ou WebP" },
    maxSizeMessage: { type: String, default: "La taille de l'image ne doit pas dépasser 2 MB" },
    processingErrorMessage: { type: String, default: "Erreur lors du traitement de l'image" },
  };

  connect() {
    this.cropper = null;
    this.currentFile = null;
    this.imageUrl = null;
    this.modal = document.getElementById(this.modalIdValue);
    console.log(
      "[ImageCropper] Controller connected",
      this.modal ? "✓ Modal found" : "✗ Modal NOT found"
    );

    if (this.modal) {
      this.modalImage = this.modal.querySelector(
        '[data-image-cropper-target="image"]'
      );
      this.modalZoomSlider = this.modal.querySelector(
        '[data-image-cropper-target="zoomSlider"]'
      );
      this.modalRotationSlider = this.modal.querySelector(
        '[data-image-cropper-target="rotationSlider"]'
      );
      this.modalZoomValue = this.modal.querySelector(
        '[data-image-cropper-target="zoomValue"]'
      );
      this.modalRotationValue = this.modal.querySelector(
        '[data-image-cropper-target="rotationValue"]'
      );
      this.modalCropButton = this.modal.querySelector(
        '[data-image-cropper-target="cropButton"]'
      );
      this.modalCancelButton = this.modal.querySelector(
        '[data-image-cropper-target="cancelButton"]'
      );
      this.modalRotateButton = this.modal.querySelector(
        '[data-image-cropper-target="rotateButton"]'
      );

      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    if (this.modalZoomSlider) {
      this.modalZoomSlider.addEventListener("input", (e) => this.updateZoom(e));
    }
    if (this.modalRotationSlider) {
      this.modalRotationSlider.addEventListener("input", (e) =>
        this.updateRotation(e)
      );
    }
    if (this.modalRotateButton) {
      this.modalRotateButton.addEventListener("click", (e) =>
        this.rotateBy90(e)
      );
    }
    if (this.modalCropButton) {
      this.modalCropButton.addEventListener("click", (e) => this.crop(e));
    }
    if (this.modalCancelButton) {
      this.modalCancelButton.addEventListener("click", (e) =>
        this.closeModal(e)
      );
    }
    if (this.modal) {
      this.modal.addEventListener("click", (e) => this.handleBackdropClick(e));
    }

    this.boundHandleEscape = (e) => this.handleEscape(e);
    document.addEventListener("keydown", this.boundHandleEscape);
  }

  disconnect() {
    this.destroyCropper();
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
    }
    if (this.boundHandleEscape) {
      document.removeEventListener("keydown", this.boundHandleEscape);
    }
  }

  get imageTarget() {
    return this.modalImage || this.targets.find("image");
  }

  get zoomSliderTarget() {
    return this.modalZoomSlider || this.targets.find("zoomSlider");
  }

  get rotationSliderTarget() {
    return this.modalRotationSlider || this.targets.find("rotationSlider");
  }

  get zoomValueTarget() {
    return this.modalZoomValue;
  }

  get rotationValueTarget() {
    return this.modalRotationValue;
  }

  get cropButtonTarget() {
    return this.modalCropButton;
  }

  get cancelButtonTarget() {
    return this.modalCancelButton;
  }

  get hasZoomValueTarget() {
    return !!this.modalZoomValue;
  }

  get hasRotationValueTarget() {
    return !!this.modalRotationValue;
  }

  get hasCropButtonTarget() {
    return !!this.modalCropButton;
  }

  get hasCancelButtonTarget() {
    return !!this.modalCancelButton;
  }

  triggerFileInput(event) {
    event.preventDefault();
    if (this.hasInputTarget) {
      this.inputTarget.click();
    }
  }

  selectImage(event) {
    console.log("[ImageCropper] File selected");
    const file = event.target.files[0];

    if (!file) {
      console.log("[ImageCropper] No file selected");
      return;
    }

    console.log("[ImageCropper] File:", file.name, file.type, file.size);

    if (!this.validateFile(file)) {
      this.inputTarget.value = "";
      return;
    }

    this.currentFile = file;
    console.log("[ImageCropper] Opening modal...");
    this.openModal();
  }

  validateFile(file) {
    if (!this.validTypesValue.includes(file.type)) {
      alert(this.invalidTypeMessageValue);
      return false;
    }

    if (file.size > this.maxSizeValue) {
      alert(this.maxSizeMessageValue);
      return false;
    }

    return true;
  }

  openModal() {
    console.log("[ImageCropper] openModal called");

    if (!this.modal) {
      console.error("[ImageCropper] Modal not found!");
      return;
    }

    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
    }

    this.imageUrl = URL.createObjectURL(this.currentFile);
    console.log("[ImageCropper] Image URL created:", this.imageUrl);

    this.imageTarget.src = this.imageUrl;
    console.log("[ImageCropper] Image src set, removing hidden class...");

    this.modal.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    console.log("[ImageCropper] Modal opened");

    this.imageTarget.addEventListener(
      "load",
      () => {
        console.log("[ImageCropper] Image loaded, initializing cropper...");
        this.initCropper();
      },
      { once: true }
    );
  }

  closeModal() {
    this.destroyCropper();

    if (this.modal) {
      this.modal.classList.add("hidden");
    }
    document.body.style.overflow = "";

    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
      this.imageUrl = null;
    }

    this.inputTarget.value = "";
    this.currentFile = null;
  }

  initCropper() {
    this.destroyCropper();

    const isRound = this.cropShapeValue === "round";

    this.cropper = new Cropper(this.imageTarget, {
      aspectRatio: this.aspectRatioValue,
      viewMode: 1,
      autoCropArea: 1,
      responsive: true,
      restore: false,
      guides: !isRound,
      center: true,
      highlight: false,
      cropBoxMovable: false,
      cropBoxResizable: false,
      dragMode: "move",
      toggleDragModeOnDblclick: false,
      ready: () => {
        if (isRound) {
          const viewBox =
            this.imageTarget.parentElement.querySelector(".cropper-view-box");
          const face =
            this.imageTarget.parentElement.querySelector(".cropper-face");
          if (viewBox) viewBox.style.borderRadius = "50%";
          if (face) face.style.borderRadius = "50%";
        }
        this.updateZoomValue();
        this.updateRotationValue();
      },
    });
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  updateZoom(event) {
    if (!this.cropper) return;

    const zoom = parseFloat(event.target.value);
    this.cropper.zoomTo(zoom);
    this.updateZoomValue();
  }

  updateRotation(event) {
    if (!this.cropper) return;

    const rotation = parseInt(event.target.value);
    this.cropper.rotateTo(rotation);
    this.updateRotationValue();
  }

  rotateBy90() {
    if (!this.cropper) return;

    this.cropper.rotate(90);
    const rotation = Math.round(this.cropper.getData().rotate) % 360;
    this.rotationSliderTarget.value = rotation;
    this.updateRotationValue();
  }

  updateZoomValue() {
    if (!this.cropper || !this.hasZoomValueTarget) return;

    const canvasData = this.cropper.getCanvasData();
    const imageData = this.cropper.getImageData();
    const zoomRatio =
      canvasData.naturalWidth > 0
        ? canvasData.width / canvasData.naturalWidth
        : 1;
    const zoomPercent = Math.round(zoomRatio * 100);

    this.zoomValueTarget.textContent = `${zoomPercent}%`;
  }

  updateRotationValue() {
    if (!this.cropper || !this.hasRotationValueTarget) return;

    const rotation = Math.round(this.cropper.getData().rotate) % 360;
    this.rotationValueTarget.textContent = `${rotation}°`;
  }

  async crop(event) {
    event.preventDefault();

    if (!this.cropper) return;

    this.setLoadingState(true);

    try {
      const canvas = this.cropper.getCroppedCanvas({
        width: 512,
        height: 512,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      const blob = await this.canvasToBlob(canvas);
      await this.uploadBlob(blob);
    } catch (error) {
      console.error("Error cropping image:", error);
      alert(this.processingErrorMessageValue);
    } finally {
      this.setLoadingState(false);
    }
  }

  canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            blob.name = this.currentFile.name;
            resolve(blob);
          } else {
            reject(new Error("Failed to create blob"));
          }
        },
        "image/jpeg",
        0.95
      );
    });
  }

  async uploadBlob(blob) {
    // TOUJOURS faire un DirectUpload pour créer le blob ActiveStorage
    // puis soit soumettre directement (mode Direct Submit) soit créer un champ caché (mode Form Attachment)
    const upload = new DirectUpload(blob, this.uploadUrlValue);

    return new Promise((resolve, reject) => {
      upload.create((error, blobData) => {
        if (error) {
          reject(error);
        } else {
          if (this.shouldDirectSubmit()) {
            // Mode 1: Soumission directe avec updateUrl
            this.submitCroppedImage(blobData.signed_id);
          } else {
            // Mode 2: Créer un champ caché avec le signed_id pour soumission avec le formulaire parent
            this.attachSignedIdToForm(blobData.signed_id, blob);
          }
          resolve(blobData);
        }
      });
    });
  }

  attachSignedIdToForm(signedId, blob) {
    // Trouver le formulaire parent
    const form = this.inputTarget.closest("form");
    if (!form) {
      console.error("[ImageCropper] Form not found");
      return;
    }

    // Retirer le fichier de l'input original pour éviter confusion
    this.inputTarget.value = "";

    // Créer ou récupérer le champ caché pour le signed_id
    const fieldName = this.inputTarget.name;
    let hiddenInput = form.querySelector(
      `input[type="hidden"][name="${fieldName}"]`
    );

    if (!hiddenInput) {
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = fieldName;
      form.appendChild(hiddenInput);
    }

    hiddenInput.value = signedId;

    // Mettre à jour l'affichage de l'avatar avec l'image croppée
    if (this.hasAvatarDisplayTarget) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.avatarDisplayTarget.innerHTML = `<img src="${e.target.result}" class="w-full h-full object-cover" />`;
      };
      reader.readAsDataURL(blob);
    }

    // Fermer la modal
    this.closeModal();
  }

  shouldDirectSubmit() {
    return (
      this.hasUpdateUrlValue &&
      this.updateUrlValue !== "#" &&
      this.updateUrlValue !== ""
    );
  }

  submitCroppedImage(signedId) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = this.updateUrlValue;

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "authenticity_token";
    csrfInput.value = csrfToken;

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "PATCH";

    const avatarInput = document.createElement("input");
    avatarInput.type = "hidden";
    avatarInput.name = "avatar";
    avatarInput.value = signedId;

    form.appendChild(csrfInput);
    form.appendChild(methodInput);
    form.appendChild(avatarInput);
    document.body.appendChild(form);

    form.submit();
  }

  setLoadingState(loading) {
    if (this.hasCropButtonTarget) {
      this.cropButtonTarget.disabled = loading;
      const icon = this.cropButtonTarget.querySelector("i");
      if (icon) {
        if (loading) {
          icon.className = "fas fa-spinner fa-spin";
        } else {
          icon.className = "fas fa-check";
        }
      }
    }

    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.disabled = loading;
    }
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  handleBackdropClick(event) {
    if (event.target === this.modal) {
      this.closeModal();
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }
}
